import React, { useState, useCallback, useContext } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Box,
  Alert,
  Tooltip,
} from "@mui/material";
import HealingIcon from "@mui/icons-material/Healing";
import { jsPDF } from "jspdf";
import { getStudentsByClassroom, getSubjects } from "../../api/sponteAPI";
import { getGradesByStudent } from "../../api/autenticoAPI";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import logo from "../ModalResultado/logo.png";
import { getUnidadeData } from "../../utils/YEARCONSTS";
import { normalizeStudentsByClassroom, normalizeSubjects } from "../../utils/sponteDataNormalizer";
import { logDebug } from "../../utils/logger";

/** Nota mínima no bimestre (após MB / RM / RB): abaixo disso conta como recuperação no relatório. */
const MEDIA_MINIMA_BIMESTRAL = 7;

/** Partículas que não viram inicial (ex.: de, da). */
const PARTICLE = new Set(["de", "da", "das", "do", "dos", "e"]);

/**
 * Mantém primeiro e último nome por extenso; nomes do meio viram inicial (ex.: Fernanda S. C. de Medeiros).
 */
function compactMiddleName(fullName) {
  const raw = String(fullName || "-")
    .trim()
    .replace(/\s+/g, " ");
  if (!raw) return "-";
  const parts = raw.split(" ");
  if (parts.length <= 2) return raw;

  const first = parts[0];
  const last = parts[parts.length - 1];
  const middle = parts.slice(1, -1);

  const midAbbrev = middle.map((w) => {
    const lw = w.toLowerCase();
    if (PARTICLE.has(lw)) return w;
    if (w.length <= 2) return w;
    const ch = w[0];
    return `${ch.toUpperCase()}.`;
  });

  return [first, ...midAbbrev, last].join(" ");
}

/**
 * Uma linha só; encolhe até caber na largura da coluna (mm), sem quebra.
 */
function fitStudentLine(pdf, chamada, nome, maxWidthMm) {
  const prefix = `${String(chamada ?? "?")} — `;
  const compact = compactMiddleName(String(nome || "-").trim());

  const widthOf = (body) => pdf.getTextWidth(prefix + body);

  if (widthOf(compact) <= maxWidthMm) return prefix + compact;

  const parts = compact.split(" ").filter(Boolean);
  if (parts.length > 2) {
    const shorter = `${parts[0]} … ${parts[parts.length - 1]}`;
    if (widthOf(shorter) <= maxWidthMm) return prefix + shorter;
  }

  const ell = "…";
  let body = parts.length > 2 ? `${parts[0]} … ${parts[parts.length - 1]}` : compact;
  while (body.length > 1 && pdf.getTextWidth(prefix + body + ell) > maxWidthMm) {
    body = body.slice(0, -1).trimEnd();
  }
  return prefix + body + ell;
}

/** Ícone PNG (bandagem / curativo) gerado uma vez — compatível com jsPDF */
function createRecoveryMarkerDataUrl(px = 40) {
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const cx = px / 2;
  const cy = px / 2;
  const r = px / 2 - 1;

  const grd = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, 1, cx, cy, r);
  grd.addColorStop(0, "#ffb74d");
  grd.addColorStop(1, "#ef6c00");
  ctx.fillStyle = grd;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.lineWidth = Math.max(2, px * 0.11);
  ctx.lineCap = "round";
  const arm = px * 0.26;
  ctx.beginPath();
  ctx.moveTo(cx - arm, cy);
  ctx.lineTo(cx + arm, cy);
  ctx.moveTo(cx, cy - arm);
  ctx.lineTo(cx, cy + arm);
  ctx.stroke();

  try {
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

function periodLabel(period) {
  if (period === undefined || period === null || period === "") return "-";
  const n = parseInt(String(period), 10);
  if (Number.isNaN(n)) return String(period);
  return `${n}º Bimestre`;
}

/** Melhor nota entre MB, RM e RB no período para uma disciplina (mesma ideia do boletim). */
function effectivePeriodGrade(gradesPayload, disciplinaId, periodNum) {
  const periodKey = `period${periodNum}`;
  const list = gradesPayload?.[periodKey];
  if (!Array.isArray(list)) return null;
  const row = list.find((g) => String(g.DisciplinaID) === String(disciplinaId));
  if (!row) return null;

  const parse = (v) => {
    if (v === undefined || v === null || v === "") return NaN;
    const n = parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : NaN;
  };

  const mb = parse(row.mb);
  const rm = parse(row.rm);
  const rb = parse(row.rb);

  const vals = [mb, rm, rb].filter((x) => Number.isFinite(x) && x > 0);
  if (vals.length === 0) return null;
  return Math.max(...vals);
}

function isEmRecuperacaoNoPeriodo(effective) {
  return effective != null && effective < MEDIA_MINIMA_BIMESTRAL;
}

/** Abrevia nomes longos de disciplinas (mapa + limite). */
function abbreviateDiscipline(name) {
  const raw = String(name || "-").trim();
  const lower = raw.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const map = {
    "educacao fisica": "Ed. Física",
    "ingles": "Inglês",
    "lingua inglesa": "Inglês",
    "lingua portuguesa": "Português",
    "lingua português": "Português",
    "lingua portuguesa e literatura": "Port. / Lit.",
    "arte": "Artes",
    "ensino religioso": "E. Religioso",
    "projeto de vida": "Proj. Vida",
  };

  if (map[lower]) return map[lower];
  if (raw.length <= 18) return raw;
  return `${raw.slice(0, 17)}…`;
}

/**
 * Relatório matriz alunos × disciplinas com marcador onde o aluno está em recuperação no período.
 */
const AlunosRecuperacaoPDF = ({ turmaId, turmaNome, anoLetivo, period }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const auth = useContext(AuthContext);

  const periodReady = period !== undefined && period !== null && period !== "";

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
      setProgress(0);
      setProcessedCount(0);
      setTotalCount(0);
      setError(null);
    }
  };

  const handleGenerate = useCallback(async () => {
    if (!turmaId) {
      setError("Selecione uma turma primeiro.");
      return;
    }
    if (!periodReady) {
      setError("Selecione o período (bimestre) para montar o relatório.");
      return;
    }

    const periodNum = parseInt(String(period), 10);
    if (Number.isNaN(periodNum) || periodNum < 1 || periodNum > 4) {
      setError("Período inválido. Use um bimestre de 1 a 4.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setProgress(0);
      setProcessedCount(0);

      const markerPng = createRecoveryMarkerDataUrl(48);

      const response = await getStudentsByClassroom(turmaId);
      const studentsList = normalizeStudentsByClassroom(response);
      if (studentsList.length === 0) {
        throw new Error("Nenhum aluno encontrado nesta turma.");
      }

      const sortedStudents = [...studentsList].sort((a, b) => {
        const numA = parseInt(a.NumeroChamada, 10) || 0;
        const numB = parseInt(b.NumeroChamada, 10) || 0;
        return numA - numB;
      });

      const subjectsRaw = await getSubjects(turmaId);
      const subjects = normalizeSubjects(subjectsRaw).sort((a, b) =>
        String(a.Nome || "").localeCompare(String(b.Nome || ""), "pt-BR")
      );

      if (subjects.length === 0) {
        throw new Error("Nenhuma disciplina encontrada para esta turma.");
      }

      setTotalCount(sortedStudents.length);

      /** @type {Record<number|string, Record<number|string, boolean>>} */
      const grid = {};
      const totalsBySubject = {};
      subjects.forEach((s) => {
        totalsBySubject[String(s.DisciplinaID)] = 0;
      });

      for (let i = 0; i < sortedStudents.length; i++) {
        const student = sortedStudents[i];
        const sid = student.AlunoID;
        grid[sid] = {};

        const gradesPayload = await getGradesByStudent(sid, turmaId);

        for (const sub of subjects) {
          const eff = effectivePeriodGrade(gradesPayload, sub.DisciplinaID, periodNum);
          const mark = isEmRecuperacaoNoPeriodo(eff);
          grid[sid][String(sub.DisciplinaID)] = mark;
          if (mark) {
            totalsBySubject[String(sub.DisciplinaID)] += 1;
          }
        }

        setProcessedCount(i + 1);
        setProgress(Math.round(((i + 1) / sortedStudents.length) * 100));
      }

      logDebug("[AlunosRecuperacaoPDF] grid montado", { grid, totalsBySubject });

      const unidadeData = getUnidadeData(auth.user?.unit);
      const dadosUnidade = {
        nome: unidadeData.nome || "Colégio Autêntico",
        anoLetivo: anoLetivo ?? unidadeData.anoLetivo ?? "",
      };

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      let y = margin;

      const teal = [30, 149, 140];
      const border = [207, 216, 220];

      try {
        pdf.addImage(logo, "PNG", margin, y, 14, 14);
      } catch (e) {
        logDebug("Logo recuperação:", e);
      }

      const nowStr = new Date().toLocaleString("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      });

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.setTextColor(33, 33, 33);
      pdf.text(String(dadosUnidade.nome), pageW / 2, y + 6, { align: "center" });

      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(100, 100, 100);
      pdf.text(nowStr, pageW - margin, y + 6, { align: "right" });

      y += 18;
      pdf.setDrawColor(...teal);
      pdf.setLineWidth(0.8);
      pdf.line(margin, y, pageW - margin, y);
      y += 5;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13);
      pdf.setTextColor(...teal);
      pdf.text("Alunos em recuperação", pageW / 2, y, { align: "center" });
      y += 8;

      pdf.setFontSize(8.5);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(66, 66, 66);
      pdf.text(
        `${periodLabel(periodNum)} · maior entre MB, RM e RB · recuperação se média < ${MEDIA_MINIMA_BIMESTRAL},0`,
        margin,
        y
      );
      y += 5;
      pdf.setFontSize(8);
      pdf.text(`Turma: ${turmaNome || "-"}   ·   Ano letivo: ${dadosUnidade.anoLetivo}   ·   Período: Recuperação (${periodLabel(periodNum)})`, margin, y);
      y += 10;

      const nameColW = 56;
      const tableW = pageW - 2 * margin;
      const subColW = (tableW - nameColW) / subjects.length;
      const headerRowH = 12;
      const minDataRowH = 8;

      const setBorder = () => {
        pdf.setDrawColor(...border);
        pdf.setLineWidth(0.12);
      };

      const drawCell = (cx, cy, cw, ch, opts = {}) => {
        const { fill, zebra } = opts;
        if (fill) {
          pdf.setFillColor(...fill);
          pdf.rect(cx, cy, cw, ch, "F");
        } else if (zebra) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(cx, cy, cw, ch, "F");
        } else {
          pdf.setFillColor(255, 255, 255);
          pdf.rect(cx, cy, cw, ch, "F");
        }
        setBorder();
        pdf.rect(cx, cy, cw, ch);
      };

      const drawColumnHeadersRow = (yy) => {
        let xx = margin;
        drawCell(xx, yy, nameColW, headerRowH, { fill: teal });
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setTextColor(255, 255, 255);
        pdf.text("Alunos", xx + 3, yy + headerRowH / 2 + 1);
        xx += nameColW;

        pdf.setFontSize(7);
        subjects.forEach((sub) => {
          drawCell(xx, yy, subColW, headerRowH, { fill: teal });
          const label = abbreviateDiscipline(sub.Nome);
          const lines = pdf.splitTextToSize(label, subColW - 3);
          const useLines = lines.slice(0, 2);
          const lineGap = 3.6;
          const blockH = useLines.length * lineGap;
          let ty = yy + (headerRowH - blockH) / 2 + lineGap - 1;
          useLines.forEach((ln) => {
            pdf.text(String(ln).trim(), xx + subColW / 2, ty, {
              align: "center",
              maxWidth: subColW - 3,
            });
            ty += lineGap;
          });
          xx += subColW;
        });
        pdf.setTextColor(0, 0, 0);
        return yy + headerRowH;
      };

      y = drawColumnHeadersRow(y);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);

      sortedStudents.forEach((student, idx) => {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        const nomeLinha = fitStudentLine(
          pdf,
          student.NumeroChamada,
          student.Nome,
          nameColW - 6
        );
        const studentRowH = minDataRowH;

        const iconMm = Math.min(4, studentRowH - 2);

        const needsBreak = () => y + studentRowH > pageH - margin;

        if (needsBreak()) {
          pdf.addPage();
          y = margin;
          pdf.setFont("helvetica", "italic");
          pdf.setFontSize(10);
          pdf.setTextColor(...teal);
          pdf.text("(continuação)", margin, y);
          y += 7;
          pdf.setTextColor(0, 0, 0);
          y = drawColumnHeadersRow(y);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
        }

        let x = margin;
        const zebra = idx % 2 === 1;

        drawCell(x, y, nameColW, studentRowH, { zebra });
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(40, 40, 40);
        const tyText = y + studentRowH / 2 + 2;
        pdf.text(nomeLinha, x + 3, tyText);
        x += nameColW;

        subjects.forEach((sub) => {
          drawCell(x, y, subColW, studentRowH, { zebra });
          const hit = grid[student.AlunoID]?.[String(sub.DisciplinaID)] === true;
          if (hit && markerPng) {
            pdf.addImage(
              markerPng,
              "PNG",
              x + subColW / 2 - iconMm / 2,
              y + studentRowH / 2 - iconMm / 2,
              iconMm,
              iconMm
            );
          } else if (hit && !markerPng) {
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(11);
            pdf.setTextColor(239, 108, 0);
            pdf.text(":(", x + subColW / 2, y + studentRowH / 2 + 2, { align: "center" });
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(8);
            pdf.setTextColor(40, 40, 40);
          }
          x += subColW;
        });
        y += studentRowH;
      });

      const footerH = 9;
      if (y + footerH > pageH - margin) {
        pdf.addPage();
        y = margin;
      }

      let x = margin;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setFillColor(236, 239, 241);
      setBorder();
      pdf.rect(x, y, nameColW, footerH, "FD");
      pdf.setTextColor(55, 71, 79);
      pdf.text("Total em recuperação:", x + 3, y + footerH / 2 + 1);
      x += nameColW;

      subjects.forEach((sub) => {
        pdf.setFillColor(236, 239, 241);
        pdf.rect(x, y, subColW, footerH, "FD");
        const t = totalsBySubject[String(sub.DisciplinaID)] ?? 0;
        pdf.text(String(t), x + subColW / 2, y + footerH / 2 + 1, { align: "center" });
        x += subColW;
      });

      pdf.setTextColor(0, 0, 0);

      const blobUrl = pdf.output("bloburl");
      window.open(blobUrl, "_blank");
      setLoading(false);
      setProcessedCount(0);
    } catch (err) {
      console.error("[AlunosRecuperacaoPDF]", err);
      setError(err.message || "Erro ao gerar o relatório.");
      setLoading(false);
    }
  }, [turmaId, turmaNome, anoLetivo, period, periodReady, auth.user?.unit]);

  const disabled = !turmaId || !periodReady;

  return (
    <>
      <Tooltip
        title={
          disabled && turmaId && !periodReady
            ? "Selecione também o período (bimestre) para gerar o relatório."
            : disabled
              ? "Selecione uma turma e um período."
              : "Relatório de recuperação por disciplina no período."
        }
      >
        <span>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<HealingIcon />}
            onClick={() => setOpen(true)}
            disabled={disabled}
            sx={{
              height: "40px",
              textTransform: "none",
              borderRadius: "4px",
              fontSize: "0.9rem",
              whiteSpace: "nowrap",
            }}
          >
            Alunos em recuperação
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Alunos em recuperação — {turmaNome || "Turma"}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {!periodReady && turmaId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Escolha o <strong>Período</strong> nos filtros (1º ao 4º bimestre).
            </Alert>
          )}
          {!loading ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0 }}>
              PDF em paisagem. Recuperação se média do bimestre for menor que{" "}
              <strong>{MEDIA_MINIMA_BIMESTRAL},0</strong>. Abre numa nova aba para imprimir ou salvar.
            </Typography>
          ) : (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CircularProgress variant="determinate" value={progress} sx={{ mb: 2 }} />
              <Typography variant="body2">
                Carregando notas… {progress}% ({processedCount}/{totalCount} alunos)
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading} color="inherit">
            Fechar
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            color="warning"
            disabled={loading || disabled}
          >
            Gerar PDF
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AlunosRecuperacaoPDF;
